// ProjectionsPage.js — Main container for the Projections tab
// Role-aware: Family Office sees entity filter, Individual sees guided path
import React, { useState, useMemo, useCallback } from 'react';
import { runProjection } from '../../lib/projections/engine';
import {
  COST_DEFAULTS, DEFAULT_HEDGE_RATIO, DEFAULT_REINVESTMENT_TENOR,
  ADVERSE_SHOCK_YEAR, MARKET_CONFIG, BENCHMARK_CONFIG,
} from '../../lib/projections/constants';
import { PORTFOLIO_POSITIONS, FX_HEDGES, ENTITIES } from '../../data/portfolioMockData';

import DisclosureGate from './DisclosureGate';
import ProjectionInputsPanel from './ProjectionInputsPanel';
import HeadlineTiles from './HeadlineTiles';
import FanChart from './FanChart';
import YearlyTable from './YearlyTable';
import AssumptionsStrip from './AssumptionsStrip';
import ModeComparison from './ModeComparison';
import AttributionWaterfall from './AttributionWaterfall';
import SaveCompareExport from './SaveCompareExport';

// Default foreign exchange rates (local per USD) for each market — demo values
const DEFAULT_FX_RATES = {
  Kenya: 131.20,
  India: 83.50,
  Brazil: 5.10,
  Mexico: 17.80,
  Turkey: 32.00,
  Bangladesh: 118.00,
  'South Africa': 18.50,
  Egypt: 49.10,
  Ethiopia: 56.50,
};

// Default current yields (364d) — demo values
const DEFAULT_CURRENT_YIELDS = {
  Kenya: 0.1710,
  India: 0.072,
  Brazil: 0.125,
  Mexico: 0.105,
  Turkey: 0.350,
  Bangladesh: 0.085,
  'South Africa': 0.095,
  Egypt: 0.268,
  Ethiopia: 0.200,
};

/**
 * Build positions for a hypothetical allocation spread equally across selected markets.
 */
function buildHypotheticalPositions(amount, markets, fxRates) {
  if (!markets || markets.length === 0) return [];
  const perMarket = amount / markets.length;
  return markets.map((market, i) => ({
    id: `HYP-${i}`,
    entity: 'HYP',
    country: market,
    instrument: '364-Day T-Bill',
    currency: MARKET_CONFIG[market]?.ccy || 'USD',
    faceValue: perMarket * (fxRates[market] || 1), // convert base to local
    fxRateAtPurchase: fxRates[market] || 1,
    currentFxRate: fxRates[market] || 1,
    status: 'active',
    couponRate: 0,
    yieldToMaturity: DEFAULT_CURRENT_YIELDS[market] || 0.10,
    maturityDate: '2027-09-13',
    purchaseDate: '2026-09-13',
  }));
}

/**
 * Main Projections page component.
 * @param {{ role: string }} props
 */
const ProjectionsPage = ({ role = 'investor_family_office' }) => {
  const isFamilyOffice = role === 'investor_family_office';
  const isIndividual = role === 'investor_individual';

  // ── Disclosure gate ──
  const [disclosureAcknowledged, setDisclosureAcknowledged] = useState(() => {
    return sessionStorage.getItem('ff_projections_disclosed') === 'true';
  });

  // ── Entity filter (Family Office only) ──
  const [selectedEntity, setSelectedEntity] = useState('all');

  // ── Guided path step (Individual only) ──
  const [guidedStep, setGuidedStep] = useState(1);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ── Input state ──
  const [inputs, setInputs] = useState({
    source: isIndividual ? 'hypothetical' : 'current',
    hypotheticalAmount: 100000,
    hypotheticalMarkets: ['Kenya', 'India', 'South Africa'],
    mode: isIndividual ? 'interest_only' : 'full_reinvest',
    withdrawalPct: 5,
    tenorDays: DEFAULT_REINVESTMENT_TENOR,
    hedgeRatio: DEFAULT_HEDGE_RATIO,
    baseCurrency: 'USD',
    scenarios: ['base', 'optimistic', 'adverse'],
    adverseShockYear: ADVERSE_SHOCK_YEAR,
    costs: { ...COST_DEFAULTS },
  });

  const handleInputChange = useCallback((key, value) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  }, []);

  // ── Filter positions by entity ──
  const filteredPositions = useMemo(() => {
    if (inputs.source === 'hypothetical') {
      return buildHypotheticalPositions(
        inputs.hypotheticalAmount,
        inputs.hypotheticalMarkets,
        DEFAULT_FX_RATES
      );
    }
    let positions = PORTFOLIO_POSITIONS.filter(p =>
      p.status === 'active' || p.status === 'maturing'
    );
    if (isIndividual) {
      // Individual sees only ENT-003
      positions = positions.filter(p => p.entity === 'ENT-003');
    } else if (selectedEntity !== 'all') {
      positions = positions.filter(p => p.entity === selectedEntity);
    }
    return positions;
  }, [inputs.source, inputs.hypotheticalAmount, inputs.hypotheticalMarkets, selectedEntity, isIndividual]);

  // ── Run projection (memoised) ──
  const projectionResult = useMemo(() => {
    if (filteredPositions.length === 0) return null;

    // Build current foreign exchange rates from positions or defaults
    const currentFxRates = {};
    filteredPositions.forEach(p => {
      if (!currentFxRates[p.country]) {
        currentFxRates[p.country] = p.currentFxRate || DEFAULT_FX_RATES[p.country] || 1;
      }
    });

    // Build current yields from positions or defaults
    const currentYields = {};
    filteredPositions.forEach(p => {
      if (!currentYields[p.country]) {
        currentYields[p.country] = p.yieldToMaturity || DEFAULT_CURRENT_YIELDS[p.country] || 0.10;
      }
    });

    return runProjection({
      positions: filteredPositions,
      currentFxRates,
      currentYields,
      baseCurrency: inputs.baseCurrency,
      mode: inputs.mode,
      modeParams: { withdrawalPct: (inputs.withdrawalPct || 5) / 100 },
      hedgeRatio: inputs.hedgeRatio,
      costs: inputs.costs,
      tenorDays: inputs.tenorDays,
      scenarios: inputs.scenarios,
      adverseShockYear: inputs.adverseShockYear,
    });
  }, [filteredPositions, inputs]);

  // ── Disclosure gate ──
  if (!disclosureAcknowledged) {
    return (
      <DisclosureGate
        onAcknowledge={() => {
          sessionStorage.setItem('ff_projections_disclosed', 'true');
          sessionStorage.setItem('ff_projections_disclosed_at', new Date().toISOString());
          setDisclosureAcknowledged(true);
        }}
      />
    );
  }

  // ── Individual guided path ──
  const renderGuidedWizard = () => (
    <div className="bg-white border border-zinc-200 rounded-none p-5 mb-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-3">
        Step {guidedStep} of 3
      </p>
      <div className="flex gap-2 mb-4">
        {[1, 2, 3].map(step => (
          <div
            key={step}
            className={`h-1 flex-1 rounded-none ${step <= guidedStep ? 'bg-indigo-500' : 'bg-zinc-200'}`}
          />
        ))}
      </div>

      {guidedStep === 1 && (
        <div className="space-y-3">
          <p className="font-mono text-[11px] text-zinc-600">How much would you like to project?</p>
          <input
            type="number"
            value={inputs.hypotheticalAmount}
            onChange={e => handleInputChange('hypotheticalAmount', Number(e.target.value))}
            className="w-full font-mono text-[11px] px-2 py-1.5 border border-zinc-200 rounded-none text-zinc-700"
            placeholder="Investment amount in USD"
            min={1000}
          />
          <div>
            <p className="font-mono text-[10px] text-zinc-500 mb-1">Select markets:</p>
            <div className="grid grid-cols-2 gap-1">
              {Object.keys(MARKET_CONFIG).map(market => (
                <label key={market} className="flex items-center gap-1 font-mono text-[11px] text-zinc-600">
                  <input
                    type="checkbox"
                    checked={(inputs.hypotheticalMarkets || []).includes(market)}
                    onChange={e => {
                      const current = inputs.hypotheticalMarkets || [];
                      handleInputChange('hypotheticalMarkets',
                        e.target.checked ? [...current, market] : current.filter(m => m !== market)
                      );
                    }}
                  />
                  {market}
                </label>
              ))}
            </div>
          </div>
          <button
            onClick={() => setGuidedStep(2)}
            className="px-4 py-1.5 bg-zinc-600 text-white font-mono text-[11px] rounded-none hover:bg-zinc-700"
          >
            Next
          </button>
        </div>
      )}

      {guidedStep === 2 && (
        <div className="space-y-3">
          <p className="font-mono text-[11px] text-zinc-600">Choose how you want to handle returns:</p>
          <select
            value={inputs.mode}
            onChange={e => handleInputChange('mode', e.target.value)}
            className="w-full font-mono text-[11px] px-2 py-1.5 border border-zinc-200 rounded-none bg-white text-zinc-700"
          >
            <option value="full_reinvest">Full Reinvestment</option>
            <option value="interest_only">Interest Only (withdraw income)</option>
            <option value="annual_withdraw">Annual Withdrawal</option>
            <option value="amortise">Amortising</option>
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => setGuidedStep(1)}
              className="px-4 py-1.5 border border-zinc-300 text-zinc-600 font-mono text-[11px] rounded-none hover:bg-zinc-50"
            >
              Back
            </button>
            <button
              onClick={() => setGuidedStep(3)}
              className="px-4 py-1.5 bg-zinc-600 text-white font-mono text-[11px] rounded-none hover:bg-zinc-700"
            >
              View Results
            </button>
          </div>
        </div>
      )}

      {guidedStep === 3 && (
        <div className="flex gap-2">
          <button
            onClick={() => setGuidedStep(2)}
            className="px-4 py-1.5 border border-zinc-300 text-zinc-600 font-mono text-[11px] rounded-none hover:bg-zinc-50"
          >
            Back
          </button>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="font-mono text-[10px] text-indigo-600 hover:text-indigo-700 underline px-2 py-1.5"
          >
            {showAdvanced ? 'Hide Advanced' : 'Advanced Options'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Entity filter for Family Office */}
      {isFamilyOffice && (
        <div className="flex items-center gap-3 mb-2">
          <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            Entity
          </label>
          <select
            value={selectedEntity}
            onChange={e => setSelectedEntity(e.target.value)}
            className="font-mono text-[11px] px-2 py-1 border border-zinc-200 rounded-none bg-white text-zinc-700"
          >
            <option value="all">All Entities</option>
            {Object.values(ENTITIES).map(ent => (
              <option key={ent.id} value={ent.id}>{ent.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Individual guided path */}
      {isIndividual && guidedStep < 3 && renderGuidedWizard()}

      {/* Full inputs panel (Family Office or Individual step 3) */}
      {(isFamilyOffice || (isIndividual && guidedStep >= 3)) && (
        <ProjectionInputsPanel
          inputs={inputs}
          onChange={handleInputChange}
          simplified={isIndividual}
          showAdvanced={showAdvanced}
          onToggleAdvanced={() => setShowAdvanced(!showAdvanced)}
        />
      )}

      {/* Results (only when projection is available) */}
      {projectionResult && (isFamilyOffice || guidedStep >= 3) && (
        <>
          {/* Headline Tiles */}
          <HeadlineTiles
            baseResult={projectionResult.scenarios.base}
            benchmark={projectionResult.benchmark}
            baseCurrency={inputs.baseCurrency}
          />

          {/* Fan Chart */}
          <FanChart
            result={projectionResult}
            baseCurrency={inputs.baseCurrency}
            adverseShockYear={inputs.adverseShockYear}
          />

          {/* Yearly Table */}
          <YearlyTable
            result={projectionResult}
            baseCurrency={inputs.baseCurrency}
          />

          {/* Mode Comparison */}
          <ModeComparison
            positions={filteredPositions}
            currentFxRates={DEFAULT_FX_RATES}
            currentYields={DEFAULT_CURRENT_YIELDS}
            inputs={inputs}
            baseCurrency={inputs.baseCurrency}
          />

          {/* Attribution Waterfall */}
          <AttributionWaterfall
            result={projectionResult}
            baseCurrency={inputs.baseCurrency}
          />

          {/* Save / Compare / Export */}
          <SaveCompareExport
            result={projectionResult}
            inputs={inputs}
          />

          {/* Assumptions Strip */}
          <AssumptionsStrip assumptions={projectionResult.assumptions} />
        </>
      )}

      {/* No positions warning */}
      {filteredPositions.length === 0 && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-none p-6 text-center">
          <p className="font-mono text-[11px] text-zinc-500">
            No active positions found. Switch to "Hypothetical Allocation" or select a different entity.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectionsPage;
